<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  IAffiliateAutoApproveForm,
  IAffiliateAutoApproveFormSchema,
  IAffiliateAutoApproveLocalizedAdmins,
} from '@modules/general-settings/submodules/auto-approve/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_PERMISSIONS,
  TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE,
} from '@modules/general-settings/submodules/auto-approve/constants';
import useSettingsAutoApproveSaveMutation
  from '@modules/general-settings/submodules/auto-approve/mutations/useSettingsAutoApproveSaveMutation';
import useSettingsAutoApproveListQuery
  from '@modules/general-settings/submodules/auto-approve/queries/useSettingsAutoApproveListQuery';
import { useI18n } from '@modules/i18n/composables';
import { watchImmediate } from '@vueuse/core';

import AutoApproveFilter from '../../components/AutoApproveFilter/AutoApproveFilter.vue';
import AutoApproveSearchByAdmin from '../../components/AutoApproveSearchByAdmin/AutoApproveSearchByAdmin.vue';

const { getTranslation } = useI18n();

const localizedAdmins = ref<IAffiliateAutoApproveLocalizedAdmins>({});
const searchAdminId = ref<number>();
const isSearchedByAdmin = ref(false);

const { data, isLoading } = useSettingsAutoApproveListQuery();
const {
  mutate: saveData,
  isLoading: isLoadingSubmission,
  errors,
  clearError,
} = useSettingsAutoApproveSaveMutation();

const adminDropdownOptions = computed<IFormSelectInputOption<number>[]>(() => (
  data?.value?.admins_list?.map((item) => ({ value: item.id, label: item.value })) ?? []
));

const formSchema = computed<IAffiliateAutoApproveFormSchema>(() => ({
  auto_approve_locales: {
    type: EFormInputTypes.SWITCH,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE, 'ACCEPT_REQUEST').value,
    value: data.value?.auto_approve_locales?.auto_approve_locales,
  },
  admin_id_for_all_locales: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE, 'SELECT_ADMIN').value,
    value: data.value?.auto_approve_locales?.admin_id_for_all_locales,
    options: adminDropdownOptions.value,
  },
  filter_by_admin: {
    type: EFormInputTypes.SLOT,
  },
  localized_admins: {
    type: EFormInputTypes.SLOT,
  },
}));

function setLocalizedAdminValue(value: IAffiliateAutoApproveLocalizedAdmins): void {
  localizedAdmins.value = value;
}

function onSubmit(values: IAffiliateAutoApproveForm): void {
  saveData({
    auto_approve_locales: {
      auto_approve_locales: values.auto_approve_locales,
      admin_id_for_all_locales: values.admin_id_for_all_locales,
      localized_admins: localizedAdmins.value,
    },
  });
}

function onChangeSearchAdmin(value?: number): void {
  searchAdminId.value = value;
}

function searchByAdmin(): void {
  if (searchAdminId.value) {
    isSearchedByAdmin.value = true;
  }
}

function resetSearchByAdmin(): void {
  searchAdminId.value = undefined;
  isSearchedByAdmin.value = false;
}

watchImmediate(() => data.value?.auto_approve_locales?.localized_admins, (newValue) => {
  if (newValue) {
    localizedAdmins.value = newValue;
  }
});
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsAutoApprovePage'">
    <VLoader
      :loading="isLoading"
      min-height="680px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
        :is-loading="isLoadingSubmission"
        :custom-errors="errors"
        :readonly="!$hasPermission(ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_PERMISSIONS.AUTOAPPROVELOCALES_EDIT)"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #filter_by_admin="{ className }">
          <AutoApproveSearchByAdmin
            :admins="adminDropdownOptions"
            :search-admin-id="searchAdminId"
            :class="className"
            @change="onChangeSearchAdmin"
            @search="searchByAdmin"
            @reset="resetSearchByAdmin"
          />
        </template>
        <template #localized_admins="{ className, readonly }">
          <AutoApproveFilter
            :class="className"
            :admins="data?.admins_list"
            :locales="data?.locales_list"
            :localized-admins="localizedAdmins"
            :filter-admin-id="isSearchedByAdmin ? searchAdminId : undefined"
            :readonly="readonly"
            @set-admin="setLocalizedAdminValue"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
